import { useEffect, useMemo, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import { SubsList } from "src/page/plans/plansSlice";
import { EMPTY_STRING, planCategoryMapping } from "src/components/constant/constant";
import { formatCurrency as formatCurrencyUtil, sanitizeRichHtml } from "src/utils";
import styles from "./subscriptionPlanCard.module.scss";

export type SubscriptionPlanCardProps = {
  subscription: SubsList;
  featured?: boolean;
  selected?: boolean;
  loading?: boolean;
  disabled?: boolean;
  hideSelectButton?: boolean;
  selectButtonLabel?: string;
  onSelect?: (_subscription: SubsList, _selectedFrequency?: string) => void;
};

const FREQUENCY_LABEL: Record<string, string> = {
  MONTHLY: "Monthly",
  QUARTERLY: "Quarterly",
  SEMIANNUAL: "Semi-Annual",
  YEARLY: "Yearly"
};

const FREQUENCY_SUFFIX: Record<string, string> = {
  MONTHLY: "/ month",
  QUARTERLY: "/ quarter",
  SEMIANNUAL: "/ 6 months",
  YEARLY: "/ year"
};

export default function SubscriptionPlanCard({
  subscription,
  featured = false,
  selected = false,
  loading = false,
  disabled = false,
  hideSelectButton = false,
  selectButtonLabel,
  onSelect
}: SubscriptionPlanCardProps) {
  const priceBreakups = useMemo(
    () => subscription.attributes?.price_breakup || [],
    [subscription.attributes?.price_breakup]
  );
  const availableFrequencies = useMemo(() => priceBreakups.map((bp) => bp.frequency), [priceBreakups]);
  const defaultFrequency = availableFrequencies[0] || EMPTY_STRING;
  const [selectedFrequency, setSelectedFrequency] = useState<string>(defaultFrequency);

  useEffect(() => {
    if (defaultFrequency) setSelectedFrequency(defaultFrequency);
  }, [defaultFrequency]);

  const selectedPriceBreakup = useMemo(
    () => priceBreakups.find((bp) => bp.frequency === selectedFrequency) || priceBreakups[0],
    [selectedFrequency, priceBreakups]
  );

  const basePrice = selectedPriceBreakup?.base_price?.amount ?? 0;
  const currency = selectedPriceBreakup?.base_price?.currency || "AUD";
  const priceFormatted = formatCurrencyUtil(basePrice, currency);
  const isFree = !priceBreakups.length || basePrice === 0;

  const categoryLabel = planCategoryMapping[subscription.subscription_category] || subscription.subscription_category;

  const tone = featured ? "onDark" : EMPTY_STRING;

  return (
    <section
      className={`${styles.card} ${featured ? styles.featured : EMPTY_STRING} ${
        selected ? styles.selected : EMPTY_STRING
      }`}
      aria-labelledby={`plan-${subscription.subsId}`}
    >
      {featured ? <span className={styles.featuredRibbon}>Most popular</span> : null}

      <span className={`${styles.categoryBadge} ${featured ? styles.onDark : EMPTY_STRING}`}>{categoryLabel}</span>

      <h3 id={`plan-${subscription.subsId}`} className={`${styles.title} ${styles[tone]}`}>
        {subscription.title}
      </h3>
      {subscription.subTitle ? (
        <p className={`${styles.subtitle} ${styles[tone]}`}>{subscription.subTitle}</p>
      ) : null}

      {isFree ? (
        <span className={styles.freeBadge}>Included</span>
      ) : (
        <div className={`${styles.priceWrap} ${featured ? styles.onDark : EMPTY_STRING}`}>
          <span className={styles.priceAmount}>{priceFormatted}</span>
          <span className={styles.priceFrequency}>
            {FREQUENCY_SUFFIX[selectedFrequency] || `/ ${selectedFrequency.toLowerCase()}`}
          </span>
        </div>
      )}

      {availableFrequencies.length > 1 ? (
        <div className={`${styles.frequencySelector} ${featured ? styles.onDark : EMPTY_STRING}`}>
          {availableFrequencies.map((frequency) => (
            <button
              key={frequency}
              type="button"
              className={`${styles.frequencyChip} ${selectedFrequency === frequency ? styles.active : EMPTY_STRING} ${
                featured ? styles.onDark : EMPTY_STRING
              }`}
              onClick={() => setSelectedFrequency(frequency)}
              disabled={loading || disabled}
            >
              {FREQUENCY_LABEL[frequency] || frequency}
            </button>
          ))}
        </div>
      ) : null}

      <hr className={`${styles.divider} ${featured ? styles.onDark : EMPTY_STRING}`} />

      <div
        className={`${styles.description} ${featured ? styles.onDark : EMPTY_STRING}`}
        dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(subscription.description) }}
      />

      {!hideSelectButton ? (
        <button
          type="button"
          className={`${styles.selectButton} ${featured ? styles.onDark : EMPTY_STRING} ${
            selected ? styles.selected : EMPTY_STRING
          }`}
          onClick={() => onSelect?.(subscription, selectedFrequency || undefined)}
          disabled={loading || disabled}
        >
          {loading ? (
            <CircularProgress size={18} sx={{ color: selected ? "#fff" : featured ? "#0f172a" : "#fff" }} />
          ) : selected ? (
            <>
              <CheckIcon fontSize="small" />
              Selected
            </>
          ) : (
            selectButtonLabel || "Select plan"
          )}
        </button>
      ) : null}
    </section>
  );
}
